"use client";
import useStoreKeys from "@/hooks/useStoreKeys";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FC, HTMLAttributeAnchorTarget } from "react";
import SelectNavigation from "../SelectNavigation/SelectNavigation";
import styles from "./SideNavigation.module.scss";
import { StoreRegion } from "@/lib/builder/builder.config";

interface Props {
	title: string;
	links: {
		text: string;
		href: string;
		target?: HTMLAttributeAnchorTarget;
	}[];
}

const SideNavigation: FC<Props> = ({ title, links }) => {
	const { store, lang } = useStoreKeys();
	const path = usePathname();

	const linkIsActive = (link: string) => {
		const pathStripped = path?.replace(`/${store}/${lang}/`, "/");

		if (link === "account/" || link === "account") {
			return path === "account";
		}

		return pathStripped.indexOf(link) > -1;
	};

	// Check the URL for certain pages and do not show the navigation
	if (checkUrlContainsPattern(path)) return <></>;

	return (
		<>
			<SelectNavigation
				title={title}
				links={links}
				className={styles.mobileNav}
			/>
			<aside className={styles.aside}>
				<nav className={styles.nav}>
					{links.map((link, index) => {
						if (!link) {
							return null;
						}
						const sanitizedHref = cleanHref(link.href, store, lang);

						return (
							<Link
								key={`${link.toString()}-${index}`}
								className={linkIsActive(link.href) ? styles.active : ""}
								href={
									sanitizedHref.includes("customer-care")
										? `/${store}/${lang}/${sanitizedHref.replace(
												`${store}/${lang}/`,
												"",
										  )}` // Ensure no duplication
										: `/${sanitizedHref}`
								}
								target={link.target}
							>
								{link.text}
							</Link>
						);
					})}
				</nav>
			</aside>
		</>
	);
};

export default SideNavigation;

function checkUrlContainsPattern(url: string) {
	const pattern = /\/(invoices|orders|lists)\/\d+/;
	return pattern.test(url);
}

const cleanHref = (href: string, store: StoreRegion, lang: string) => {
	let link = href;
	// Handle the global store specifically
	if (store === "global") {
		link = href.replace(/global\/+/, ""); // Remove 'global' and extra slashes
	}
	const prefix = `${store}/${lang}`;
	if (
		href.includes("account") ||
		(href.startsWith("logout") && !href.includes(prefix))
	) {
		link = `${prefix}/${href}`;
	}
	// Remove store/lang prefix and clean redundant slashes
	return link
		.replace(/\/{2,}/g, "/") // Replace multiple slashes with one
		.replace(/^\/+/, ""); // Remove leading slashes
};
